import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/toggle-group';
import { cn } from '@/shared/utils/date-utils';
import type { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import { Bar, Tooltip as RechartsTooltip, Legend, LabelList, YAxis, XAxis } from 'recharts';
import {
  CHART_OPACITY,
  BAR_TOP_RADIUS,
  monthKeyFromStartDate,
  calendarPrevYm,
  LABEL_FONT_SIZE,
  TICK_FONT_SIZE,
} from '@/domains/global-dashboard/utils/chart-utils';
import { formatNumber } from '@/shared/utils/format-utils';
import {
  DashboardBarChart,
  DashboardBarGrid,
  DashboardBarPlotHoverCursor,
  DashboardChartViewport,
} from '@/domains/global-dashboard/components/GlobalDashboardBarChart';
import { barTopNumericLabelProps } from '@/domains/global-dashboard/components/dashboardChart';
import { useMemo, useState } from 'react';
import { TerminalType } from '@/shared/types/userTerminalTypes';
import { DashboardSummarySection, SummaryCard } from '@/domains/global-dashboard/sections/DashboardSummarySection';
import { SectionType } from '@/domains/global-dashboard/utils/chart-utils';
import { getDeploymentRateColorClass } from '@/shared/utils/color-utils-data-usages';
import { getTerminalTypeTextColorClass, TERMINAL_TYPE_INFO } from '@/shared/utils/color-utils-terminals';
import {
  terminalMonthsByYmFromOverview,
  terminalTypeCount,
  TERMINAL_TYPE_STACK,
} from '@/domains/global-dashboard/sections/terminals/terminal-utils';
import { TerminalEvolutionChart } from '@/domains/global-dashboard/sections/terminals/TerminalEvolutionChart';

/** Main component for the terminal inventory section. */
export function TerminalInventorySection({
  selectedOrganizations,
  accountChartColorById,
  onNavigateToOrganization,
}: {
  selectedOrganizations: OrganizationOverviewData[];
  accountChartColorById: Map<string, string>;
  onNavigateToOrganization: (organizationId: string, sectionType: SectionType) => void;
}) {
  const [terminalPerMarketSurface, setTerminalPerMarketSurface] = useState<'chart' | 'table'>('chart');
  const [terminalTypeMetric, setTerminalTypeMetric] = useState<'total' | 'deployed' | 'standby'>('total');

  const organizationSeries = useMemo(() => {
    return selectedOrganizations.map((organization) => ({
      organization,
      months: terminalMonthsByYmFromOverview(organization),
    }));
  }, [selectedOrganizations]);

  const breakdownRows = useMemo(() => {
    const reportingYm = monthKeyFromStartDate(new Date().toISOString());

    type Row = {
      row: OrganizationOverviewData;
      total: number;
      deployed: number;
      standby: number;
      netInventoryMonth: number;
      netDeployedMonth: number;
      momPct: number | null;
      deployRate: string;
      deployRateRaw: number;
      terminalTypes: { type: TerminalType; total: number; deployed: number }[];
    };

    const rowsUnsorted: Row[] = [];
    for (const { organization, months } of organizationSeries) {
      const cur = months.get(reportingYm);
      if (!cur) continue;

      const prevYm = calendarPrevYm(reportingYm);
      const prevSnap = months.get(prevYm);

      const total = cur.total;
      const deployed = cur.deployed;
      const standby = Math.max(0, total - deployed);
      const prevTotal = prevSnap !== undefined ? prevSnap.total : null;
      const netInventoryMonth = prevTotal !== null ? total - prevTotal : 0;
      const prevDeployed = prevSnap !== undefined ? prevSnap.deployed : null;
      const netDeployedMonth = prevDeployed !== null ? deployed - prevDeployed : 0;
      const momPct =
        prevTotal !== null && prevTotal > 0 ? ((total - prevTotal) / prevTotal) * 100 : null;
      const deployRateRaw = total > 0 ? (deployed / total) * 100 : 0;
      const terminalTypes = TERMINAL_TYPE_STACK.map((spec, i) => ({
        type: spec.type,
        ...cur.typeSlots[i]!,
      }));

      rowsUnsorted.push({
        row: organization,
        total,
        deployed,
        standby,
        netInventoryMonth,
        netDeployedMonth,
        momPct,
        deployRate: `${formatNumber(deployRateRaw)}`,
        deployRateRaw,
        terminalTypes,
      });
    }
    rowsUnsorted.sort((a, b) => b.total - a.total);
    return rowsUnsorted;
  }, [organizationSeries]);

  const summaryCards: SummaryCard[] = useMemo(() => {
    const totalInv = breakdownRows.reduce((s, r) => s + r.total, 0);
    const deployedSum = breakdownRows.reduce((s, r) => s + r.deployed, 0);

    const arr = [{
      title: 'All Terminals',
      currentValue: `${deployedSum}`,
      totalValue: `${totalInv}`,
      currentValueTextColor: 'text-green-500',
      description: 'currently deployed',
    }];
    Object.values(TerminalType).filter((type) => type !== TerminalType.NOT_SET).forEach((type) => {
      const terminals = breakdownRows.map((r) => terminalTypeCount(r.terminalTypes, type));
      const deployed = terminals.reduce((s, r) => s + r.deployed, 0);
      const total = terminals.reduce((s, r) => s + r.total, 0);
      arr.push({
        title: TERMINAL_TYPE_INFO[type].value,
        currentValue: `${deployed}`,
        totalValue: `${total}`,
        currentValueTextColor: getTerminalTypeTextColorClass(type),
        description: 'currently deployed',
      });
    });

    const netChangeInv = breakdownRows.reduce((s, r) => s + r.netInventoryMonth, 0);
    const globalDeployRate = totalInv > 0 ? (deployedSum / totalInv) * 100 : 0;
    arr.push(
      {
        title: 'Deployment Rate',
        currentValue: `${formatNumber(globalDeployRate)}%`,
        totalValue: null,
        currentValueTextColor: getDeploymentRateColorClass(globalDeployRate),
        description: null,
      },
      {
        title: 'Inventory net change vs previous month',
        currentValue: `${netChangeInv}`,
        totalValue: null,
        currentValueTextColor: netChangeInv >= 0 ? 'text-green-500' : 'text-red-500',
        description: null,
      });
    return arr;
  }, [breakdownRows]);

  const terminalTypesBarChartData = useMemo(() => {
    const pickForMetric = (x: { total: number; deployed: number }) => {
      if (terminalTypeMetric === 'total') return x.total;
      if (terminalTypeMetric === 'deployed') return x.deployed;
      return Math.max(0, x.total - x.deployed);
    };
    return breakdownRows.map((r) => {
      const g3 = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_PERFORMANCE_GEN_3);
      const g2 = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_PERFORMANCE_GEN_2);
      const ent = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_ENTERPRISE_V4);
      const mini = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_MINI);
      const standard4 = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_STANDARD_4);
      const standard4X = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_STANDARD_4X);
      return {
        ...r,
        ttGen3: pickForMetric(g3),
        ttGen2: pickForMetric(g2),
        ttEnt: pickForMetric(ent),
        ttMini: pickForMetric(mini),
        ttStandard4: pickForMetric(standard4),
        ttStandard4X: pickForMetric(standard4X),
      };
    });
  }, [breakdownRows, terminalTypeMetric]);

  return (
    <section className="space-y-4 border rounded-md p-4 bg-muted/50">
      <div className="flex items-start gap-2 justify-between">
        <div>
          <h2 className="text-lg font-semibold">Terminal Inventory Overview</h2>
          <p className="text-sm">Executive snapshot and terminal inventory breakdown</p>
        </div>
        {/* Deployment rate tiers */}
        <div className="flex-col text-sm flex items-end gap-2">
          <span className="font-medium">Deployment rate tiers:</span>
          <div className="flex flex-wrap items-center gap-2 font-medium">
            <span className={cn(getDeploymentRateColorClass(80), 'font-bold')}>&gt;=80%</span>
            <span>•</span>
            <span className={cn(getDeploymentRateColorClass(60), 'font-bold')}>&gt;=60% and &lt;80%</span>
            <span>•</span>
            <span className={cn(getDeploymentRateColorClass(59), 'font-bold')}>&lt;60%</span>
          </div>
        </div>
      </div>

      {/* Terminal inventory summary (current month) */}
      <DashboardSummarySection
        section={{
          type: SectionType.TERMINAL_INVENTORY,
          title: 'Terminal inventory summary (current month)',
          description: 'Aggregated month-to-date terminal counts. Deployed terminals do not include suspended service lines.'
        }}
        summaryCards={summaryCards}
      />

      {/* Terminal inventory breakdown (current month) */}
      <Card>
        <CardHeader>
          <div className="grid grid-cols-2 gap-4 justify-end items-center w-full">
            <div className="col-span-1">
              <CardTitle className="text-base">Terminal inventory breakdown (current month)</CardTitle>
              <CardDescription>
                Deployed, on-hand and total inventory by model.
              </CardDescription>
            </div>
            <div className="col-span-1 flex items-center justify-end">
              <ToggleGroup
                type="single"
                value={terminalPerMarketSurface}
                onValueChange={(v) => v && setTerminalPerMarketSurface(v as 'chart' | 'table')}
                size="sm"
                variant="outline"
              >
                <ToggleGroupItem value="chart">Chart</ToggleGroupItem>
                <ToggleGroupItem value="table">Table</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
          {terminalPerMarketSurface === 'chart' && (
            <div className="flex flex-col gap-2 justify-end">
              <ToggleGroup
                type="single"
                value={terminalTypeMetric}
                onValueChange={(v) => v && setTerminalTypeMetric(v as 'total' | 'deployed' | 'standby')}
                size="sm"
                variant="outline"
              >
                <ToggleGroupItem value="total">All units</ToggleGroupItem>
                <ToggleGroupItem value="deployed">Deployed</ToggleGroupItem>
                <ToggleGroupItem value="standby">On-hand</ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {terminalPerMarketSurface === 'table' ? (
            <div className="overflow-x-auto rounded-lg border">
              <Table className="w-full border-collapse">
                <TableHeader>
                  <TableRow className="bg-muted font-medium">
                    <TableHead>Market</TableHead>
                    <TableHead className="text-right">Deployed / Total</TableHead>
                    <TableHead className="text-right max-w-[128px]">{TERMINAL_TYPE_INFO[TerminalType.STARLINK_PERFORMANCE_GEN_3].value}</TableHead>
                    <TableHead className="text-right max-w-[128px]">{TERMINAL_TYPE_INFO[TerminalType.STARLINK_PERFORMANCE_GEN_2].value}</TableHead>
                    <TableHead className="text-right max-w-[128px]">{TERMINAL_TYPE_INFO[TerminalType.STARLINK_ENTERPRISE_V4].value}</TableHead>
                    <TableHead className="text-right max-w-[128px]">{TERMINAL_TYPE_INFO[TerminalType.STARLINK_MINI].value}</TableHead>
                    <TableHead className="text-right max-w-[128px]">{TERMINAL_TYPE_INFO[TerminalType.STARLINK_STANDARD_4].value}</TableHead>
                    <TableHead className="text-right max-w-[128px]">{TERMINAL_TYPE_INFO[TerminalType.STARLINK_STANDARD_4X].value}</TableHead>
                    <TableHead className="text-right">Deploy rate</TableHead>
                    <TableHead className="text-right">Inventory change vs previous month</TableHead>
                    <TableHead className="text-right">Deployed change vs previous month</TableHead>
                    <TableHead className="text-right">MoM Growth</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakdownRows.map((r) => {
                    const performanceGen3 = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_PERFORMANCE_GEN_3);
                    const performanceGen2 = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_PERFORMANCE_GEN_2);
                    const enterpriseV4 = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_ENTERPRISE_V4);
                    const mini = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_MINI);
                    const standard4 = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_STANDARD_4);
                    const standard4X = terminalTypeCount(r.terminalTypes, TerminalType.STARLINK_STANDARD_4X);
                    return (
                      <TableRow
                        key={r.row.organizationId}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => onNavigateToOrganization(r.row.organizationId, SectionType.TERMINAL_INVENTORY)}
                      >
                        <TableCell className="font-medium">{r.row.name}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.deployed} / {r.total}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{performanceGen3.deployed} / {performanceGen3.total}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{performanceGen2.deployed} / {performanceGen2.total}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{enterpriseV4.deployed} / {enterpriseV4.total}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{mini.deployed} / {mini.total}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{standard4.deployed} / {standard4.total}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{standard4X.deployed} / {standard4X.total}</TableCell>
                        <TableCell className={cn('text-right', getDeploymentRateColorClass(r.deployRateRaw))}>{r.deployRate}%</TableCell>
                        <TableCell className="text-right">
                          {r.netInventoryMonth > 0 ? '+' : ''}
                          {r.netInventoryMonth}
                        </TableCell>
                        <TableCell className="text-right">
                          {r.netDeployedMonth > 0 ? '+' : ''}
                          {r.netDeployedMonth}
                        </TableCell>
                        <TableCell className="text-right">
                          {r.momPct == null ? '—' : `${r.momPct >= 0 ? '+' : ''}${formatNumber(r.momPct)}%`}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : breakdownRows.length === 0 ? (
            <p className="text-sm py-8 text-center">No terminal summary for the current month.</p>
          ) : (
            <DashboardChartViewport>
              <DashboardBarChart data={terminalTypesBarChartData} variant="perMarket">
                <DashboardBarGrid />
                <XAxis dataKey="row.name" tick={{ fontSize: TICK_FONT_SIZE }} textAnchor="middle" />
                <YAxis tick={{ fontSize: TICK_FONT_SIZE }} allowDecimals={false} label={{ value: 'Units', angle: -90, position: 'middle', fontSize: LABEL_FONT_SIZE }} />
                <RechartsTooltip
                  cursor={<DashboardBarPlotHoverCursor />}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload as (typeof terminalTypesBarChartData)[number];
                    return (
                      <div className="rounded-md border bg-background px-3 py-2 text-xs shadow-md min-w-[180px]">
                        <p className="font-medium mb-1">{d.row.name}</p>
                        <p className="mb-1 text-[11px]">
                          {terminalTypeMetric === 'total'
                            ? 'All units in inventory'
                            : terminalTypeMetric === 'deployed'
                              ? 'Deployed units'
                              : 'Standby units (inventory − deployed per model)'}
                        </p>
                        {TERMINAL_TYPE_STACK.map((spec) => (
                          <p key={spec.dataKey} className="tabular-nums flex justify-between gap-4">
                            <span style={{ color: spec.fill }}>{TERMINAL_TYPE_INFO[spec.type].value}</span>
                            <span>{formatNumber(Number(d[spec.dataKey as keyof typeof d] ?? 0))}</span>
                          </p>
                        ))}
                      </div>
                    );
                  }}
                />
                <Legend
                  content={() => (
                    <div className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 text-xs pt-2">
                      {TERMINAL_TYPE_STACK.map((s) => (
                        <span key={s.dataKey} className="inline-flex items-center gap-1.5 text-sm">
                          <span
                            className="h-2.5 w-2.5 rounded-sm shrink-0 ring-1 ring-border/60"
                            style={{ background: s.fill }}
                          />
                          {TERMINAL_TYPE_INFO[s.type].value}
                        </span>
                      ))}
                    </div>
                  )}
                />
                {TERMINAL_TYPE_STACK.map((spec, idx) => {
                  const isLast = idx === TERMINAL_TYPE_STACK.length - 1;
                  return (
                    <Bar
                      key={spec.dataKey}
                      dataKey={spec.dataKey}
                      stackId="types"
                      name={TERMINAL_TYPE_INFO[spec.type].key}
                      fill={spec.fill}
                      fillOpacity={CHART_OPACITY.terminalTypeStack}
                      radius={isLast ? BAR_TOP_RADIUS : [0, 0, 0, 0]}
                    >
                      {isLast && (
                        <LabelList
                          {...barTopNumericLabelProps}
                          formatter={(value: unknown) => formatNumber(typeof value === 'number' ? value : Number(value))}
                        />
                      )}
                    </Bar>
                  );
                })}
              </DashboardBarChart>
            </DashboardChartViewport>
          )}
        </CardContent>
      </Card>

      {/* Terminal inventory evolution */}
      <TerminalEvolutionChart
        selectedOrganizations={selectedOrganizations}
        accountChartColorById={accountChartColorById}
      />
    </section>
  );
}
